// Die Freundeskarte als Rindenkarte (F46, Fassung A des Grafik-Leads, vom Projekt-Lead abgenickt; Erste Fassung, Runde 7):
// ein Stück Rinde mit einem Blatt altem Papier, 260 × 364 Bildpixel, jedes Feld nach public/art/karte/karte.json. Vorne
// das Brustbild im runden Fenster vor der Landschaft, in der ihr euch kennengelernt habt, Werte, Passive und Kniff, Items
// und Weg; hinten der Steckbrief mit der Erinnerungsspur. Texte in HTML über den Bildern. Die Werte stellt run-views.ts
// zusammen; hier wird nur gesetzt. Reine Darstellung; public/art/ gehört dem Grafik-Lead, hier wird nur gelesen.
import { image, lookImage, sprite } from './art.ts';
import { drawBackdrop } from './fahrt/scene.ts';
import { applyTwig } from './twig.ts';

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const tipAttr = (lines: (string | false | null | undefined)[]) => {
  const t = lines.filter(Boolean).join('\n');
  return t ? ` data-tip="${esc(t)}"` : '';
};

/** Was die Karte zeigt, fertig in Worten und Zahlen */
export type CardData = {
  name: string;
  species: string;
  speciesName: string;
  article: string;
  look: number;
  /** Biom, in dem ihr euch kennengelernt habt (Landschaft im Fenster) */
  home: string;
  friendClass: string;
  className: string;
  classTip: string[];
  level: number;
  levelTip: string[];
  wounded: string | null;
  away: string | null;
  damage: { heal: boolean; value: number; star: boolean; tip: string[] };
  speed: { value: string; star: boolean; tip: string[] };
  mut: { start: number; max: number; star: boolean; tip: string[] };
  life: { hp: number; max: number; share: number; level: 'gut' | 'mittel' | 'knapp'; star: boolean; tip: string[] };
  passive: { id: string; name: string; text: string; first: string };
  kniff: { name: string; text: string; first: string };
  items: { slots: number; list: { short: string; tip: string }[] };
  way: { steps: number; open: boolean; tip: string[] };
  /** Unter der Karte: Eignung an Bord und was gerade wirkt (HTML) */
  under: string;
  back: {
    sub: string;
    origin: string;
    quote: string | null;
    values: { icon: string; text: string; tip: string[] }[];
    heading: string;
    memories: { day: number; text: string; kind: string }[];
  };
};

/** Ein Bild als absolut gesetztes Element (Kartenpixel) */
const pic = (id: string, x: number, y: number, extra = '', cls = 'rk-img') => {
  const sp = sprite('ui', id);
  if (!sp) return '';
  const [w, h] = sp.size ?? sp.frameSize ?? [16, 16];
  return `<i class="${cls}" style="left:${x}px;top:${y}px;width:${w}px;height:${h}px;background-image:url(art/${sp.file})"${extra} aria-hidden="true"></i>`;
};
/** Ein Bild mit seiner Mitte auf (x, y) */
const picAt = (id: string, cx: number, cy: number, extra = '') => {
  const sp = sprite('ui', id);
  if (!sp) return '';
  const [w, h] = sp.size ?? sp.frameSize ?? [16, 16];
  const [mx, my] = sp.center ?? [Math.floor(w / 2), Math.floor(h / 2)];
  return pic(id, cx - mx, cy - my, extra);
};
/** Ein Bild eines Streifens (Kerbe, Fassung, Weg) */
const frameAt = (id: string, frame: number, x: number, y: number, inner = '', extra = '') => {
  const sp = sprite('ui', id);
  if (!sp) return '';
  const [w, h] = sp.frameSize ?? [16, 16];
  const n = sp.frames ?? 1;
  return `<i class="rk-img" style="left:${x}px;top:${y}px;width:${w}px;height:${h}px;background-image:url(art/${sp.file});background-size:${w * n}px ${h}px;background-position:-${frame * w}px 0"${extra} aria-hidden="true">${inner}</i>`;
};
const box = (r: number[], cls: string, html: string, extra = '') => `<div class="${cls}" style="left:${r[0]}px;top:${r[1]}px;width:${r[2]}px;height:${r[3]}px"${extra}>${html}</div>`;
const star = (on: boolean) => (on && sprite('ui', 'karte-stern') ? `<i class="rk-star" style="background-image:url(art/${sprite('ui', 'karte-stern')!.file})" aria-hidden="true"></i>` : on ? '★' : '');

/** Mut als zehn Kerben (je 10 Mut): voll, halb, leer nach der Regel des Grafik-Leads */
function notches(mut: number, max: number): string {
  const sp = sprite('ui', 'karte-kerbe');
  const n = (mut / Math.max(1, max)) * 10;
  let full = Math.floor(n);
  const rest = n - full;
  let half = 0;
  if (rest >= 0.75) full += 1;
  else if (rest >= 0.25) half = 1;
  const st = sp?.states ?? { voll: 0, halb: 1, leer: 2 };
  const gap = (sp?.frameSize?.[0] ?? 6) + ((sp as { gap?: number } | undefined)?.gap ?? 1);
  return Array.from({ length: 10 }, (_, i) => frameAt('karte-kerbe', i < full ? st.voll! : i < full + half ? st.halb! : st.leer!, 141 + i * gap, 129)).join('');
}

function front(d: CardData): string {
  const seal = sprite('ui', `karte-siegel-${d.friendClass}`);
  const healIcon = d.damage.heal ? 'karte-heilung' : 'karte-schaden';
  const fill = sprite('ui', `karte-fuellung-${d.life.level}`);
  const fillW = d.life.hp > 0 ? Math.max(8, Math.round(124 * d.life.share)) : 0;
  const passiveIcon = sprite('ui', 'passiv-' + d.passive.id) ? `passiv-${d.passive.id}` : 'karte-passive';
  const slot = sprite('ui', 'karte-fassung');
  const slotStep = (slot?.frameSize?.[0] ?? 16) + ((slot as { gap?: number } | undefined)?.gap ?? 4);
  const items = Array.from({ length: d.items.slots }, (_, i) => {
    const it = d.items.list[i];
    return frameAt('karte-fassung', it ? (slot?.states?.voll ?? 1) : (slot?.states?.leer ?? 0), 22 + i * slotStep, 333, it ? `<b>${esc(it.short)}</b>` : '', tipAttr([it ? it.tip : 'freier Platz']));
  }).join('');
  const pip = sprite('ui', 'karte-weg');
  const pipStep = (pip?.frameSize?.[0] ?? 8) + ((pip as { gap?: number } | undefined)?.gap ?? 3);
  const pipN = Math.min(7, Math.max(d.way.steps + (d.way.open ? 1 : 0), 3));
  const pips = Array.from({ length: pipN }, (_, i) => frameAt('karte-weg', i < d.way.steps ? (pip?.states?.voll ?? 0) : i === d.way.steps && d.way.open ? (pip?.states?.offen ?? 1) : (pip?.states?.leer ?? 2), 128 + i * pipStep, 337)).join('');
  const state = d.wounded ? picAt('karte-wunde', 97, 72, tipAttr([d.wounded])) : d.away ? box([62, 64, 70, 16], 'rk-away', esc(d.away)) : '';
  return `<div class="rk-face rk-front" style="background-image:url(art/${sprite('ui', 'karte-vorn')?.file ?? 'karte/karte-vorn.png'})">
      <canvas class="rk-win" width="80" height="80" style="left:24px;top:68px" data-sp="${esc(d.species)}" data-look="${d.look}" data-biome="${esc(d.home)}" aria-hidden="true"></canvas>
      ${pic('karte-fenster', 16, 60)}
      ${state}
      ${box([22, 21, 166, 24], 'rk-name', esc(d.name), ' data-twig="klein-eiche" data-twig-px="1" data-twig-max="166"')}
      ${box([22, 45, 166, 16], 'rk-sub', `${esc(d.speciesName)} · ${esc(d.className)}`, tipAttr(d.classTip))}
      ${box([213, 19, 32, 24], 'rk-level', String(d.level), ` data-twig="klein-nuss" data-twig-px="1"${tipAttr([`Level ${d.level}`, ...d.levelTip])}`)}
      ${seal ? picAt(seal.id, 206, 46, tipAttr(d.classTip)) : ''}
      ${pic(healIcon, 121, 68)}${box([140, 64, 34, 20], 'rk-num', `${d.damage.value}${star(d.damage.star)}`, tipAttr(d.damage.tip))}${box([176, 64, 62, 24], 'rk-word', d.damage.heal ? 'heilt je<br>Angriff' : 'Schaden', tipAttr(d.damage.tip))}
      ${pic('karte-tempo', 121, 97)}${box([140, 93, 34, 20], 'rk-num', `${esc(d.speed.value)}${star(d.speed.star)}`, tipAttr(d.speed.tip))}${box([176, 93, 62, 24], 'rk-word', 'Angriffe<br>pro s', tipAttr(d.speed.tip))}
      ${pic('karte-mut', 121, 126)}<div class="rk-hover" style="left:121px;top:126px;width:117px;height:30px"${tipAttr(d.mut.tip)}></div>${notches(d.mut.start, d.mut.max)}${box([141, 141, 97, 14], 'rk-word', `Mut zu Beginn${d.mut.star ? ' ' + star(true) : ''}`)}
      ${pic('karte-leben', 21, 160)}${fill && fillW ? `<i class="rk-fill" style="left:41px;top:164px;width:${fillW}px;height:8px;border-image-source:url(art/${fill.file})" aria-hidden="true"></i>` : ''}<div class="rk-hover" style="left:21px;top:158px;width:217px;height:18px"${tipAttr(d.life.tip)}></div>${box([168, 158, 70, 18], 'rk-life', `${Math.round(d.life.hp)} / ${Math.round(d.life.max)}${star(d.life.star)}`, tipAttr(d.life.tip))}
      ${picAt(passiveIcon, 29, 194)}${box([40, 184, 198, 18], 'rk-ab', esc(d.passive.name), tipAttr([d.passive.text]))}${box([22, 203, 216, 48], 'rk-text', esc(d.passive.first), tipAttr([d.passive.text]))}
      ${pic('karte-kniff', 22, 261)}${box([40, 258, 198, 18], 'rk-ab', esc(d.kniff.name), tipAttr([d.kniff.text]))}${box([22, 277, 216, 48], 'rk-text', esc(d.kniff.first), tipAttr([d.kniff.text]))}
      ${items}
      <div class="rk-hover" style="left:128px;top:333px;width:84px;height:16px"${tipAttr(d.way.tip)}></div>${pips}
      <button class="rk-turn" data-flip="1" title="Karte umdrehen (U)" style="left:230px;top:334px">${picAt('karte-umdrehen', 10, 10)}</button>
    </div>`;
}

function back(d: CardData): string {
  const b = d.back;
  const cells: [number, number, number, number][] = [[22, 146, 78, 18], [102, 146, 56, 18], [160, 146, 78, 18], [22, 166, 78, 18], [102, 166, 56, 18], [160, 166, 78, 18]];
  const values = b.values
    .slice(0, 6)
    .map((v, i) => {
      const r = cells[i]!;
      return `${pic(v.icon, r[0], r[1] + 2)}${box([r[0] + 17, r[1], r[2] - 17, r[3]], 'rk-val', esc(v.text), tipAttr(v.tip))}`;
    })
    .join('');
  const rows = [212, 243, 274, 305];
  const memories = b.memories.length
    ? b.memories
        .slice(0, 4)
        .map((m, i) => {
          const icon = sprite('ui', `karte-erinnerung-${m.kind}`) ? `karte-erinnerung-${m.kind}` : 'karte-erinnerung-ereignis';
          return `${pic(icon, 24, rows[i]! + 2)}${box([46, rows[i]!, i === 3 ? 168 : 192, 30], 'rk-mem', `<b>Tag ${m.day}</b> ${esc(m.text)}`, tipAttr([`Tag ${m.day}: ${m.text}`]))}`;
        })
        .join('')
    : box([46, 212, 192, 30], 'rk-mem', 'Noch nichts Besonderes, aber die Reise hat ja gerade erst begonnen.');
  return `<div class="rk-face rk-back" style="background-image:url(art/${sprite('ui', 'karte-rueck')?.file ?? 'karte/karte-rueck.png'})">
      ${box([22, 30, 216, 24], 'rk-name', esc(d.name), ' data-twig="klein-eiche" data-twig-px="1" data-twig-max="216"')}
      ${box([22, 55, 216, 16], 'rk-sub', esc(b.sub), tipAttr(d.classTip))}
      ${box([22, 71, 216, 16], 'rk-sub', esc(b.origin))}
      ${b.quote ? box([34, 93, 200, 45], 'rk-quote', `„${esc(b.quote)}“`, tipAttr([`„${b.quote}“`])) : ''}
      ${values}
      ${box([22, 191, 216, 16], 'rk-head', esc(b.heading))}
      ${memories}
      <button class="rk-turn" data-flip="1" title="Karte umdrehen (U)" style="left:230px;top:334px">${picAt('karte-umdrehen', 10, 10)}</button>
    </div>`;
}

/** Die ganze Karte: Vorder- und Rückseite übereinander, darunter die Eignung an Bord */
export function rindenkarte(d: CardData): string {
  return `<div class="rk-holder"><div class="rk-wrap"><div class="rk"><div class="rk-inner">${front(d)}${back(d)}</div></div></div>${d.under}</div>`;
}

/** Landschaftsausschnitt je Biom für das Fenster (Bildpixel der Fahrt) [ZAHL?] */
const CROP: Record<string, { x: number; y: number }> = { tannenwald: { x: 280, y: 170 }, moor: { x: 300, y: 196 }, weinhaenge: { x: 60, y: 120 } };

/** Das Fenster: die Landschaft rund ausgeschnitten, davor das Brustbild in seinem Aussehen; false, solange Bilder laden */
function paintWindow(c: HTMLCanvasElement): boolean {
  const ctx = c.getContext('2d');
  if (!ctx) return false;
  ctx.imageSmoothingEnabled = false;
  const species = c.dataset.sp ?? '';
  const look = Number(c.dataset.look ?? 0);
  const crop = CROP[c.dataset.biome ?? ''] ?? CROP.tannenwald!;
  ctx.clearRect(0, 0, 80, 80);
  ctx.save();
  ctx.beginPath();
  ctx.arc(40, 40, 40, 0, Math.PI * 2);
  ctx.clip();
  if (!drawBackdrop(ctx, c.dataset.biome ?? 'tannenwald', crop.x, crop.y, 80, 80)) {
    ctx.restore();
    return false;
  }
  ctx.restore();
  const bust = sprite('ui', `karte-bueste-${species}`);
  if (bust) {
    const img = lookImage(bust.file, look, bust);
    if (!img || !image(bust.file)) return false;
    ctx.drawImage(img, 0, 0);
    return true;
  }
  // Ohne Brustbild: mittig das Standbild der Figur aus dem Kampf in doppelter Größe (Hinweis des Grafik-Leads)
  const sp = sprite('species', species);
  const fig = sp ? lookImage(sp.file, look, sp) : null;
  if (!sp || !fig) return false;
  const [ax, ay] = sp.anchor ?? [0, 0];
  const fw = (fig as HTMLImageElement).naturalWidth ?? (fig as HTMLCanvasElement).width;
  const fh = (fig as HTMLImageElement).naturalHeight ?? (fig as HTMLCanvasElement).height;
  ctx.drawImage(fig, 0, 0, fw, fh, Math.round(40 - ax * 2), Math.round(70 - ay * 2), fw * 2, fh * 2);
  return true;
}

/** Fenster malen und Namen in Zweigschrift setzen, sobald die Bilder geladen sind (läuft jedes Bild) */
export function paintKarten(root: ParentNode = document): void {
  for (const c of root.querySelectorAll<HTMLCanvasElement>('canvas.rk-win:not([data-done])')) if (paintWindow(c)) c.dataset.done = '1';
  for (const el of root.querySelectorAll<HTMLElement>('.rk [data-twig]:not([data-twig-done])')) applyTwig(el.parentElement ?? el);
}

/**
 * So groß steht die Karte auf dem Bildschirm: ganzzahlig viele Gerätepixel je Bildpixel, damit nichts unscharf wird (1,
 * 1,5 bei doppelter Pixeldichte, 2), so groß, wie es in die Höhe passt
 */
export function cardZoom(): number {
  if (typeof window === 'undefined') return 1;
  const dpr = window.devicePixelRatio || 1;
  for (const z of [2, 1.5, 1]) {
    if (Math.abs(z * dpr - Math.round(z * dpr)) > 1e-6) continue;
    if (364 * z + 150 <= window.innerHeight && 260 * z + 420 <= window.innerWidth) return z;
  }
  return 1;
}
export function applyCardZoom(): void {
  document.documentElement.style.setProperty('--rk-zoom', String(cardZoom()));
}
